// src/settings/SecretsPanel.tsx
import { FormEvent, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { SecretKey, SecretMeta } from '../lib/types'


function SecretRow({ secret }: { secret: SecretMeta }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const queryClient = useQueryClient()

  const save = useMutation<{ ok: boolean }, Error, { key: SecretKey; value: string }>({
    mutationFn: (payload) => api.put<{ ok: boolean }>('/api/settings/secrets', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['secrets'] })
      setEditing(false)
      setValue('')
    },
  })

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    save.mutate({ key: secret.key, value })
  }

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '0.75rem 0',
      borderBottom: '1px solid var(--line)',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.9375rem', color: 'var(--fg)', fontWeight: 500 }}>
            {secret.key}
          </span>
          <span style={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            padding: '0.1rem 0.375rem',
            borderRadius: '4px',
            background: secret.isSet ? 'rgba(184,145,90,0.15)' : 'var(--elevated)',
            color: secret.isSet ? 'var(--bronze)' : 'var(--dim)',
            border: `1px solid ${secret.isSet ? 'rgba(184,145,90,0.3)' : 'var(--line)'}`,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.03em',
          }}>
            {secret.isSet ? 'Configuré' : 'Manquant'}
          </span>
        </div>
        {secret.updatedAt && (
          <p style={{ fontSize: '0.75rem', color: 'var(--dim)', margin: '0.125rem 0 0' }}>
            Mis à jour le {new Date(secret.updatedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
          </p>
        )}
      </div>

      {!editing ? (
        <button
          onClick={() => setEditing(true)}
          style={{
            background: 'var(--elevated)',
            border: '1px solid var(--line)',
            borderRadius: '7px',
            padding: '0.375rem 0.75rem',
            color: 'var(--fg)',
            fontSize: '0.8125rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontWeight: 500,
            flexShrink: 0,
          }}
        >
          {secret.isSet ? 'Modifier' : 'Configurer'}
        </button>
      ) : (
        <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            type="password"
            autoComplete="off"
            autoFocus
            required
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Nouvelle valeur"
            style={{
              background: 'var(--elevated)',
              border: '1px solid var(--arancio)',
              borderRadius: '7px',
              padding: '0.375rem 0.625rem',
              color: 'var(--fg)',
              fontSize: '0.875rem',
              fontFamily: 'var(--mono)',
              outline: 'none',
              width: '180px',
            }}
          />
          <button
            type="submit"
            disabled={save.isPending}
            style={{
              background: 'var(--arancio)',
              border: 'none',
              borderRadius: '7px',
              padding: '0.375rem 0.625rem',
              color: '#fff',
              fontSize: '0.8125rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontWeight: 600,
            }}
          >
            {save.isPending ? '…' : 'Sauver'}
          </button>
          <button
            type="button"
            onClick={() => { setEditing(false); setValue('') }}
            style={{
              background: 'transparent',
              border: '1px solid var(--line)',
              borderRadius: '7px',
              padding: '0.375rem 0.625rem',
              color: 'var(--muted)',
              fontSize: '0.8125rem',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Annuler
          </button>
          {save.error && (
            <span style={{ fontSize: '0.75rem', color: 'var(--arancio)' }}>
              {save.error.message}
            </span>
          )}
        </form>
      )}
    </div>
  )
}

export function SecretsPanel() {
  const { data, isLoading } = useQuery<{ secrets: SecretMeta[] }, Error>({
    queryKey: ['secrets'],
    queryFn: () => api.get<{ secrets: SecretMeta[] }>('/api/settings/secrets'),
  })

  return (
    <section style={{
      background: 'var(--surface)',
      border: '1px solid var(--line)',
      borderRadius: '12px',
      padding: '1.5rem',
    }}>
      <h2 style={{
        fontSize: '1rem',
        fontWeight: 600,
        color: 'var(--fg)',
        margin: '0 0 0.25rem',
        letterSpacing: '-0.015em',
      }}>
        Clés API & secrets
      </h2>
      <p style={{ color: 'var(--muted)', fontSize: '0.8125rem', margin: '0 0 1.25rem' }}>
        Les valeurs sont chiffrées au repos (AES-256-GCM) et jamais retournées par l'API.
      </p>

      {isLoading ? (
        <div style={{ color: 'var(--dim)', fontSize: '0.875rem' }}>Chargement…</div>
      ) : (
        <div>
          {(data?.secrets ?? []).map((secret) => (
            <SecretRow key={secret.key} secret={secret} />
          ))}
        </div>
      )}
    </section>
  )
}
